import { useCallback, useEffect, useRef, useState } from "react";

import { speak, stripMarkdownToText } from "@/utils/notifications.util";

// One device-local preference, shared across every Maya surface (the copilot
// popover and the full-page assistant) via a single localStorage key. TTS is
// browser-specific, so this intentionally lives client-side rather than in
// per-user backend settings.
const READ_ALOUD_STORAGE_KEY = "maya:readAloud";

const readPref = (): boolean => {
  try {
    return window.localStorage.getItem(READ_ALOUD_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writePref = (value: boolean): void => {
  try {
    window.localStorage.setItem(READ_ALOUD_STORAGE_KEY, String(value));
  } catch {
    /* storage disabled or over quota — preference just won't persist */
  }
};

const cancelSpeech = (): void => {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

/**
 * "Read Maya responses aloud" preference + helpers.
 *
 * `speakIfEnabled` reads the latest toggle value through a ref, so it is safe
 * to call from async streaming `onFinal` callbacks that closed over an older
 * render. Speech is stopped automatically when the consuming surface unmounts,
 * when the toggle is turned off, and on demand via `stopSpeaking`.
 */
export const useReadAloud = () => {
  const [readAloud, setReadAloud] = useState<boolean>(readPref);
  const readAloudRef = useRef(readAloud);

  useEffect(() => {
    readAloudRef.current = readAloud;
  }, [readAloud]);

  // Stop any in-progress speech if the surface unmounts mid-utterance.
  useEffect(() => cancelSpeech, []);

  const toggleReadAloud = useCallback(() => {
    setReadAloud((prev) => {
      const next = !prev;
      writePref(next);
      if (!next) cancelSpeech();
      return next;
    });
  }, []);

  const speakIfEnabled = useCallback((markdown: string) => {
    if (readAloudRef.current && markdown) {
      speak(stripMarkdownToText(markdown));
    }
  }, []);

  const stopSpeaking = useCallback(cancelSpeech, []);

  return { readAloud, toggleReadAloud, speakIfEnabled, stopSpeaking };
};
